<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		:transparent-header="true"
		background-color-class="bg-color-data-viz-dark-slate"
	>
		<BasePlaceHolder
			:line-count="4"
			:placehold="isLoading"
			:section-count="3"
			theme="dark"
			:class="{ 'container marketing-section': isLoading }"
		>
			<AssetPageHero
				:hero="assetHeroConfig"
				:form-config="leadCaptureFormConfig"
				:offering-id="incomeFundOfferingId"
				class="pt-300"
			/>
			<FeaturedFundSection :data="featuredFundSectionData" />
			<WhyPrivateCreditSection />
			<OurTrackRecordSection />
			<AboutOurStrategySection theme="light" heading="Market dislocation creates opportunity" />
			<section class="marketing-section bg-color-bg-secondary">
				<div class="container">
					<div class="grid">
						<div class="col-8-md col-offset-2-md text-align-center mb-200 mb-300-md">
							<h2 class="heading-2 mb-100"> Explore all projects in our portfolio </h2>
							<p class="body-lg text-color-content-secondary">
								Here are the investments that are powering our investors’ returns.
							</p>
						</div>
					</div>
				</div>

				<AssetElementContainer
					:asset-elements="assetElements"
					:asset-elements-per-page="12"
					:use-icon-view-options="true"
					:use-grid="true"
					class="container"
				/>
			</section>
			<RelatedArticlesSection
				v-if="investorUpdatesSection.relatedArticles.length"
				:heading="investorUpdatesSection.sectionHeading"
				:related-articles="investorUpdatesSection.relatedArticles"
			/>
			<OurBankAndRealEstatePartnersSection class="pt-300 pt-600-md" />
			<AddToYourPortfolioSection
				title="Add private credit to your portfolio"
				component-id="private-credit"
				:lead-capture-form-config="leadCaptureFormConfig"
				:offering-id="incomeFundOfferingId"
			/>
		</BasePlaceHolder>
	</BaseLayout>
</template>

<script setup lang="ts">
import { appBasePath, appCloudfrontPath } from '@constants';
import { computed, ref } from 'vue';
import AboutOurStrategySection from '@components/marketing-site/private-credit/about-our-strategy-section.vue';
import AddToYourPortfolioSection from '@components/marketing-site/add-to-your-portfolio-section.vue';
import { Asset } from 'types/assets';
import AssetElementContainer from '@components/asset-element/asset-element-container.vue';
import AssetPageHero from '@components/marketing-site/asset-page-hero.vue';
import { buildFundKeyStats } from '@utils/offerings';
import FeaturedFundSection from '@components/marketing-site/featured-fund-section.vue';
import { FeaturedFundSectionData } from 'types/marketing-site';
import { getAssets } from '@api/sanity/proxy/assets';
import { getFund } from '@sanity/proxy/funds';
import { getPrivateCreditRelatedArticles } from '@api/sanity/graphql/private-credit-marketing-page';
import { incomeFundOfferingId } from '@constants/reit';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { mapInvestorUpdateArticleTeasers } from '@utils/blog';
import { MarketingImageHero } from 'types/layout';
import OurBankAndRealEstatePartnersSection from '@components/marketing-site/private-credit/our-banking-and-real-estate-partners-section.vue';
import OurTrackRecordSection from '@components/marketing-site/private-credit/our-track-record-section.vue';
import RelatedArticlesSection from '@components/marketing-site/related-articles-section.vue';
import { SanityRelatedArticlesSection } from 'types/sanity/blog-education-center';
import { supplementalIncomePlanId } from '@constants/plans-info';
import { useHead } from '@unhead/vue';
import WhyPrivateCreditSection from '@components/marketing-site/private-credit/why-private-credit-section.vue';

const assetHeroConfig: MarketingImageHero = {
	backgroundColor: 'bg-color-data-viz-dark-slate',
	description:
		'Our private credit strategy capitalizes on market shifts to target the best risk-adjusted returns in the past decade.',
	heading: 'Private credit',
	title: 'An opportunistic strategy for income-focused investors',
	imageUrl: `${appCloudfrontPath}/images/marketing-site/hero-images/hero-img-private-credit.png`
};

const leadCaptureFormConfig: LeadCaptureFormConfig = {
	buttonText: 'Sign up',
	signupFlowType: 'PRESET_PLAN',
	presetPlanId: supplementalIncomePlanId,
	thirdPartyAuthTheme: 'SECONDARY'
};

const assetLink = `<a href="${appBasePath}/offerings/${incomeFundOfferingId}/view" target="_blank" class="link text-color-current-color text-decoration-underline" target="_blank">Income Fund's Portfolio</a>`;

const featuredFundSectionData = ref<FeaturedFundSectionData>({
	title: 'Income Fund',
	description: '',
	fundStats: [],
	ctaLink: {
		text: 'Learn more about the Income Real Estate Fund',
		router: 'view-offering-details',
		params: { offeringId: incomeFundOfferingId }
	},
	assetTypes: [
		{ icon: 'income-homebuilderfinance', label: 'Homebuilder finance' },
		{ icon: 'income-realestatedebt', label: 'Real estate debt' },
		{ icon: 'income-preferredequity', label: 'Preferred equity' },
		{ icon: 'income-publicreitequities', label: 'Public REIT equities' },
		{ icon: 'income-multifamily', label: 'Multifamily' }
	],
	exampleAsset: {
		heading: 'Example project',
		title: 'Waypoint',
		description: [
			`As part of our new private credit strategy, we’ve invested roughly $20.8 million to provide financing in the form of preferred equity for the development of the Mason at Daytona Beach, a 300-unit multifamily community on 65.4 acres of centrally located land in Daytona Beach, Florida. Under the terms of the investment agreement, the borrower has agreed to pay us a 13.5% fixed annual rate that will accrue for as long as it takes to finish the project, and our investment will be paid back upon its completion. This is one of many projects in the ${assetLink}.`
		],
		link: {
			text: 'Read the full update',
			router: 'investor-update',
			params: { investorUpdateId: '926' },
			target: '_blank'
		},
		imageUrl: '/images/marketing-site/waypoint-spotlight.png',
		imageAlt: 'Villa with a pool and pine trees'
	}
});

const isLoading = ref(true);

const relatedArticles = ref<SanityRelatedArticlesSection | null>(null);

const assetElements = ref<Array<Asset>>([]);

const investorUpdatesSection = computed((): SanityRelatedArticlesSection => {
	return {
		sectionHeading: relatedArticles.value?.sectionHeading ?? '',
		relatedArticles: mapInvestorUpdateArticleTeasers(relatedArticles.value?.relatedArticles ?? [])
	};
});

async function setup(): Promise<void> {
	try {
		const [privateCreditRelatedArticles, assetsData, fundDetails] = await Promise.all([
			getPrivateCreditRelatedArticles(),
			getAssets({ assetClass: 'PRIVATE_CREDIT' }),
			getFund(incomeFundOfferingId, { include: ['ANNUALIZED_RETURNS'] })
		]);

		relatedArticles.value = privateCreditRelatedArticles;
		assetElements.value = assetsData.results;

		if (fundDetails) {
			featuredFundSectionData.value.fundStats = buildFundKeyStats(fundDetails);
			featuredFundSectionData.value.description = fundDetails.fundSummary ?? '';
		}

		useHead({
			title: 'Private Credit'
		});

		isLoading.value = false;
	} catch (e) {}
}

setup();
</script>
